import type {
	PatientBillingPrimaryCard,
	PatientBillingSubscription,
} from '@/api/types/PatientApi'
import { CardSheet } from '@/components/BillingPage/CardEntry/CardSheet'
import { MembershipSheet } from '@/components/BillingPage/Sections/MembershipSheet'
import { type RowStatus, StatusPill } from '@/components/Rows'
import { Glyph } from '@/components/svgs/Glyph'
import { SUMMARY_CARD_HEIGHT } from '@/constants/cards'
import { colors } from '@/constants/colors'
import { analytics } from '@/lib/analytics'
import { formatCents } from '@/lib/money'
import { Pressable } from '@/ui/core/pressable'
import { Sheet } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'

const SHADOW = { boxShadow: '0 4px 10px rgba(120,62,58,0.10)' }

/** What each plan state is worth as a mark: paid and well, waiting on something, or off. */
const PILL: Record<PatientBillingSubscription['status'], RowStatus> = {
	ACTIVE: 'done',
	PAUSED: 'wait',
	SUSPENDED: 'alert',
	CANCELED: 'off',
	PENDING: 'wait',
	UNKNOWN: 'off',
}

const STATUS: Record<PatientBillingSubscription['status'], string> = {
	ACTIVE: 'Active',
	PAUSED: 'Paused',
	SUSPENDED: 'Suspended',
	CANCELED: 'Cancelled',
	PENDING: 'Pending',
	UNKNOWN: 'Unknown',
}

const per = (planType?: string) => {
	const v = planType?.toLowerCase() ?? ''
	if (v.startsWith('month')) return '/mo'
	if (v.startsWith('year') || v.startsWith('annual')) return '/yr'
	return ''
}

const brandOf = (cardName: string) => {
	const n = cardName.trim()
	return n ? n[0]?.toUpperCase() + n.slice(1) : 'Card'
}

/** Which card: its brand and the digits that identify it, held apart by the app's own dot. */
const cardName = (card: PatientBillingPrimaryCard) =>
	`${brandOf(card.cardName)} · ${card.lastFourDigits}`

/** When it runs out, said in a word so the date is not a bare number. */
const cardExpiry = (card: PatientBillingPrimaryCard) =>
	`Expires ${String(card.expirationMonth).padStart(2, '0')}/${String(
		card.expirationYear
	).slice(-2)}`

/**
 * What the membership costs, and what pays for it.
 *
 * The shape Care leads with: the mark and the name, then the figure in the brand's display face,
 * the way the score is set. The two were a card split down the middle by a seam that exists
 * nowhere else in this app, which gave them equal billing — and they are not equal. The
 * membership is the subject of this screen; the card is a fact about it, so it is a row beneath a
 * hairline, which is what a row is for.
 */
export const Account = ({
	subscription,
	primaryCard,
	isLoading,
}: {
	subscription?: PatientBillingSubscription | null
	primaryCard?: PatientBillingPrimaryCard | null
	isLoading: boolean
}) => {
	const [sheet, setSheet] = useState<'membership' | 'card' | null>(null)
	if (isLoading)
		return <Skeleton isLoading className="h-40 w-full rounded-note bg-blush" />
	const status = subscription && (STATUS[subscription.status] ?? subscription.status)

	const onCardSaved = () => {
		analytics.trackEvent('updated-billing-info')
		setSheet(null)
		Toast.show({ type: 'success', text1: 'Card updated' })
	}

	return (
		<>
			<View
				className="rounded-note bg-white px-4 pt-4"
				style={[SHADOW, { height: SUMMARY_CARD_HEIGHT }]}
			>
				<Pressable
					className="flex-1"
					onPress={() => setSheet('membership')}
					accessibilityRole="button"
					accessibilityLabel={
						subscription
							? `Membership. ${formatCents(subscription.costInCents)}${per(
									subscription.planType
							  )}. ${status}. What's included`
							: "No membership. What's included"
					}
				>
					<HStack className="items-center justify-between">
						<HStack className="items-center" gap={10}>
							<View className="h-10 w-10 items-center justify-center rounded-full bg-blush">
								<Glyph name="sparkle" size={20} color={colors.ink} />
							</View>
							<Text size="body3" tone="soft">
								Membership
							</Text>
						</HStack>
						<Glyph name="chevron" size={16} color={colors.ink} />
					</HStack>
					{/* The slack sits above the figure, not below it, so the amount stands on the
					    hairline it is being divided from rather than floating in the middle. */}
					<View className="flex-1" />
					<HStack className="items-end justify-between pb-1">
						{/* Set in MaisonNeue Bold rather than the display face.
						
						    The display face is the brand's signature numeral and the app spends it
						    on readings — a score, a date, a count. A price is a fact about a plan
						    rather than a reading, and PP Right Grotesk's figures are drawn tight
						    and tall, so a dollar sign, four digits and a suffix in it read as a
						    headline about money instead of an amount. MaisonNeue's are wider and
						    sit on a level baseline, which is what every other figure in billing is
						    already set in. Medium rather than bold: at fifty-six points bold is a
						    slab, and the figure only has to be the largest thing on the card, not
						    the heaviest.
						
						    Tracked at -0.035 rather than the face's own -0.02, which is the nearest
						    thing to a weight between the two we have. MaisonNeue ships a Demi and
						    the app does not: only Book, Medium and Bold are registered, and iOS
						    ignores a `fontWeight` in between for a custom family. Pulling the
						    numerals tighter reads as one denser mass without a heavier stroke. If
						    the Demi ever lands in `assets/fonts`, this becomes that and the
						    tracking goes back.
						
						    Its own leading either way: the face is set at a line the height of its
						    own type, which clips the dollar's ascender, since the sign stands
						    taller than the numerals beside it. */}
						<Text
							className="font-body-medium text-[56px] leading-[62px] tracking-[-0.035em]"
							numberOfLines={1}
							adjustsFontSizeToFit
						>
							{subscription ? (
								<>
									{formatCents(subscription.costInCents)}
									{/* The suffix carries its own leading: twenty points of type in a
									    twenty-point line cuts the y's tail off. */}
									<Text className="font-body-medium text-[20px] leading-[26px]">
										{per(subscription.planType)}
									</Text>
								</>
							) : (
								'—'
							)}
						</Text>
						{/* Lifted onto the figure's baseline, which is the line the eye reads the
						    amount along; bottom-aligned it would hang under the numeral's descender. */}
						<View style={{ marginBottom: 11 }}>
							{subscription ? (
								<StatusPill
									status={PILL[subscription.status]}
									label={status ?? undefined}
								/>
							) : (
								<Text size="body2" tone="soft">
									No membership
								</Text>
							)}
						</View>
					</HStack>
				</Pressable>

				{/* The card sits under the hairline every list in this app is divided by, because
				    what pays for the membership is a fact about it rather than its equal. */}
				<View className="h-px w-full" style={{ backgroundColor: colors.divider }} />
				<Pressable
					onPress={() => setSheet('card')}
					accessibilityRole="button"
					accessibilityLabel={
						primaryCard
							? `${cardName(primaryCard)}. ${cardExpiry(primaryCard)}. Update card`
							: 'No card on file. Add card'
					}
					className="min-h-[68px] flex-row items-center py-3"
					style={{ gap: 14 }}
				>
					<View className="h-10 w-10 items-center justify-center rounded-full bg-blush">
						<Glyph name="card" size={20} color={colors.ink} />
					</View>
					{/* Title over line, as every row in this app is built: which card, then when it
					    runs out. The expiry needs no mark to hold it apart when it is on a line of
					    its own. */}
					<VStack gap={2} className="flex-1">
						<Text size="body0" weight="bold" numberOfLines={1}>
							{primaryCard ? cardName(primaryCard) : 'Add a card'}
						</Text>
						{!!primaryCard && (
							<Text size="body1" tone="soft" numberOfLines={1}>
								{cardExpiry(primaryCard)}
							</Text>
						)}
					</VStack>
					<Glyph name="chevron" size={14} color={colors.ink} />
				</Pressable>
			</View>

			<Sheet open={sheet === 'membership'} onClose={() => setSheet(null)}>
				<MembershipSheet />
			</Sheet>

			<CardSheet
				open={sheet === 'card'}
				onClose={() => setSheet(null)}
				onSaved={onCardSaved}
			/>
		</>
	)
}
